import { apiRequest } from '../helpers'
import { t } from '../locale'
import {
  BeneficialOwner,
  ClientInfo,
  RelatedCompany,
  RepresentativeNaturalPerson,
} from '../react-app-env'

export async function addClientInfo(clientId: string): Promise<ClientInfo> {
  try {
    const response = await apiRequest(`${process.env.REACT_APP_API_URL}/client-info`, 'POST', {
      clientId: clientId,
    })

    if (!response.success) {
      throw new Error('Error creating client info')
    }

    return response
  } catch (err) {
    throw err
  }
}

export async function getClientInfo(id: string): Promise<ClientInfo> {
  try {
    const response = await apiRequest(`${process.env.REACT_APP_API_URL}/client-info/${id}`, 'GET')

    if (!response.clientInfo) {
      throw new Error(t('noData'))
    }

    return response.clientInfo
  } catch (err) {
    throw err
  }
}

export async function updateClientInfo(id: string, data: Partial<ClientInfo>): Promise<ClientInfo> {
  try {
    const response = await apiRequest(
      `${process.env.REACT_APP_API_URL}/client-info/${id}`,
      'PATCH',
      data
    )

    if (!response) {
      throw new Error('No data returned from server')
    }

    return response.data.data
  } catch (error) {
    throw error
  }
}

export async function addRepresentative(
  clientInfoId: string,
  representative: Partial<RepresentativeNaturalPerson>
): Promise<{ key: string; representative: RepresentativeNaturalPerson }> {
  try {
    const response = await apiRequest(
      `${process.env.REACT_APP_API_URL}/client-info/${clientInfoId}/representatives`,
      'POST',
      representative
    )

    if (!response.data) {
      throw new Error('No data returned from server')
    }

    return response.data
  } catch (err) {
    console.error('Error adding representative: ' + err)
    throw err
  }
}

export async function updateRepresentative(
  clientInfoId: string,
  key: string,
  data: Partial<RepresentativeNaturalPerson>
): Promise<void> {
  try {
    await apiRequest(
      `${process.env.REACT_APP_API_URL}/client-info/${clientInfoId}/${key}/representative`,
      'PATCH',
      data
    )
  } catch (err) {
    console.error('Error updating representative: ' + err)
    throw err
  }
}

export async function deleteRepresentative(clientInfoId: string, key: string): Promise<void> {
  try {
    await apiRequest(
      `${process.env.REACT_APP_API_URL}/client-info/${clientInfoId}/${key}/representative`,
      'DELETE'
    )
  } catch (err) {
    console.error('Error deleting representative: ' + err)
    throw err
  }
}

export async function addRelatedCompany(
  clientInfoId: string,
  relatedCompany: Partial<RelatedCompany>
): Promise<{ key: string; relatedCompany: RelatedCompany }> {
  try {
    const response = await apiRequest(
      `${process.env.REACT_APP_API_URL}/client-info/${clientInfoId}/company`,
      'POST',
      relatedCompany
    )

    if (!response.data) {
      throw new Error('No data returned from server')
    }

    return response.data
  } catch (err) {
    console.error('Error adding related company: ' + err)
    throw err
  }
}

export async function updateRelatedCompany(
  clientInfoId: string,
  key: string,
  data: Partial<RepresentativeNaturalPerson>
): Promise<void> {
  try {
    await apiRequest(
      `${process.env.REACT_APP_API_URL}/client-info/${clientInfoId}/${key}/company`,
      'PATCH',
      data
    )
  } catch (err) {
    console.error('Error updating related company: ' + err)
    throw err
  }
}

export async function deleteRelatedCompany(clientInfoId: string, key: string): Promise<void> {
  try {
    await apiRequest(
      `${process.env.REACT_APP_API_URL}/client-info/${clientInfoId}/${key}/company`,
      'DELETE'
    )
  } catch (err) {
    console.error('Error deleting related company: ' + err)
    throw err
  }
}

export async function addBeneficialOwner(
  clientInfoId: string,
  beneficialOwner: Partial<BeneficialOwner>
): Promise<{ key: string; beneficialOwner: BeneficialOwner }> {
  try {
    const response = await apiRequest(
      `${process.env.REACT_APP_API_URL}/client-info/${clientInfoId}/beneficial-owner`,
      'POST',
      beneficialOwner
    )

    if (!response.data) {
      throw new Error('No data returned from server')
    }

    return response.data
  } catch (err) {
    console.error('Error adding beneficial owner: ' + err)
    throw err
  }
}

export async function updateBeneficialOwner(
  clientInfoId: string,
  key: string,
  data: Partial<BeneficialOwner>
): Promise<void> {
  try {
    await apiRequest(
      `${process.env.REACT_APP_API_URL}/client-info/${clientInfoId}/${key}/beneficial-owner`,
      'PATCH',
      data
    )
  } catch (err) {
    console.error('Error updating beneficial owner: ' + err)
    throw err
  }
}

export async function deleteBeneficialOwner(
  clientInfoId: string,
  key: string,
  type: string
): Promise<void> {
  try {
    await apiRequest(
      `${process.env.REACT_APP_API_URL}/client-info/${clientInfoId}/${key}/beneficial-owner`,
      'DELETE',
      { type }
    )
  } catch (err) {
    console.error('Error deleting beneficial owner: ' + err)
    throw err
  }
}
